

import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import AdminProduct3DViewport from './AdminProduct3DViewport';
import AdminProduct3DSceneTools from './AdminProduct3DSceneTools';

const AdminProduct3DStage = ({
    stageKey = 'product-3d',
    modelData = null,
    hasModel = false,
    loading = false,
    loadingPercent = 0,
    loadError = '',
    viewMode = 'light',
    onViewModeChange,
    dimensions = null,
    blankDimensions = null,
    dimensionMode = 'model',
    showDimensions = true,
    onShowDimensionsChange,
    showBlankDimensions = false,
    onShowBlankDimensionsChange,
    sceneSettings = null,
    onSceneSettingChange,
    onSceneSettingsChange,
    externalCameraStateRef = null,
    showBlankFrame = false,
    onShowBlankFrameChange,
    realtimeCameraKey = '',
    remoteCameraState = null,
    onRealtimeCameraChange = null,
    materialTexturePreset = null,
    materialTextureBinding = null,
    unpolishedSurfaceTexturePreset = null,
    surfaceFinishPolicy = null,
    graniteControls = null,
    fullscreen: controlledFullscreen = null,
    onFullscreenChange = null,
    displayControlsOpen: controlledDisplayControlsOpen = null,
    onDisplayControlsOpenChange = null,
}) => {
    const stageRef = useRef(null);
    const [internalAppFullscreen, setInternalAppFullscreen] = useState(false);
    const [internalDisplayControlsOpen, setInternalDisplayControlsOpen] = useState(false);

    const isFullscreen = typeof controlledFullscreen === 'boolean'
        ? controlledFullscreen
        : internalAppFullscreen;
    const displayControlsOpen = typeof controlledDisplayControlsOpen === 'boolean'
        ? controlledDisplayControlsOpen
        : internalDisplayControlsOpen;

    const setFullscreenState = useCallback((nextValue) => {
        const resolved = typeof nextValue === 'function'
            ? !!nextValue(isFullscreen)
            : !!nextValue;

        if (typeof controlledFullscreen !== 'boolean') {
            setInternalAppFullscreen(resolved);
        }

        onFullscreenChange?.(resolved);
    }, [controlledFullscreen, isFullscreen, onFullscreenChange]);

    const setDisplayControlsState = useCallback((nextValue) => {
        const resolved = typeof nextValue === 'function'
            ? !!nextValue(displayControlsOpen)
            : !!nextValue;

        if (typeof controlledDisplayControlsOpen !== 'boolean') {
            setInternalDisplayControlsOpen(resolved);
        }

        onDisplayControlsOpenChange?.(resolved);
    }, [
        controlledDisplayControlsOpen,
        displayControlsOpen,
        onDisplayControlsOpenChange,
    ]);
    const modelDimensionsVisible = Boolean(
        showDimensions
        && (dimensionMode === 'model' || dimensionMode === 'both')
    );
    const blankDimensionsVisible = Boolean(
        showDimensions
        && showBlankDimensions
        && blankDimensions
        && (dimensionMode === 'blank' || dimensionMode === 'both')
    );

    /*
     * Не используем browser Fullscreen API. Для совместной ADMIN-сцены он
     * давал два разных режима: на локальном устройстве native fullscreen,
     * а на удалённом socket-click браузер запрещал requestFullscreen и
     * включался fallback. Из-за этого размеры canvas, overlays и курсор
     * расходились.
     *
     * Теперь fullscreen всегда один и тот же: stage переносится portal-ом
     * прямо в document.body и фиксируется на 100dvw × 100dvh. Такой режим
     * одинаково работает от реального клика и от realtime-клика.
     */
    const leaveFullscreen = useCallback(() => {
        setFullscreenState(false);
    }, [setFullscreenState]);

    const toggleFullscreen = useCallback(() => {
        setFullscreenState(!isFullscreen);
    }, [isFullscreen, setFullscreenState]);

    useEffect(() => {
        if (!isFullscreen || typeof document === 'undefined') return undefined;

        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation?.();
            leaveFullscreen();
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isFullscreen, leaveFullscreen]);

    useEffect(() => {
        if (!isFullscreen || typeof document === 'undefined') return undefined;

        const body = document.body;
        const root = document.documentElement;
        const previousBodyOverflow = body.style.overflow;
        const previousBodyOverscroll = body.style.overscrollBehavior;
        const previousRootOverflow = root.style.overflow;
        const previousRootOverscroll = root.style.overscrollBehavior;

        body.style.overflow = 'hidden';
        body.style.overscrollBehavior = 'none';
        root.style.overflow = 'hidden';
        root.style.overscrollBehavior = 'none';
        root.classList.add('admin-product-3d-stage-fullscreen');

        return () => {
            body.style.overflow = previousBodyOverflow;
            body.style.overscrollBehavior = previousBodyOverscroll;
            root.style.overflow = previousRootOverflow;
            root.style.overscrollBehavior = previousRootOverscroll;
            root.classList.remove('admin-product-3d-stage-fullscreen');
        };
    }, [isFullscreen]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        // Portal/fullscreen и возврат из него меняют layout в разные кадры.
        // Даём ResizeObserver дополнительную серию сигналов, включая Android
        // с visualViewport и сменой ориентации.
        const timers = [];
        const fireResize = () => window.dispatchEvent(new Event('resize'));

        const frame = window.requestAnimationFrame(fireResize);
        [50, 140, 320, 700].forEach((delay) => {
            timers.push(window.setTimeout(fireResize, delay));
        });

        return () => {
            window.cancelAnimationFrame(frame);
            timers.forEach((timer) => window.clearTimeout(timer));
        };
    }, [isFullscreen]);

    const renderStage = (isPortal = false) => (
        <section
            ref={stageRef}
            className={[
                'adminProduct3DStage',
                `adminProduct3DStage--${viewMode}`,
                isFullscreen ? 'is-fullscreen' : '',
                isFullscreen && isPortal ? 'is-fallback-fullscreen' : '',
                graniteControls ? 'has-granite-controls' : '',
            ].filter(Boolean).join(' ')}
            data-catalog-sync-scope={`product-3d-stage-${stageKey}`}
            aria-label="Управление 3D-сценой товара"
            onMouseDown={(event) => event.stopPropagation()}
        >
            <div className="adminProduct3DStage__viewport">
                {hasModel ? (
                    <AdminProduct3DViewport
                        modelData={modelData}
                        viewMode={viewMode}
                        dimensions={dimensions}
                        blankDimensions={blankDimensions}
                        dimensionMode={dimensionMode}
                        showDimensions={showDimensions}
                        sceneSettings={sceneSettings}
                        externalCameraStateRef={externalCameraStateRef}
                        showBlankFrame={showBlankFrame}
                        realtimeCameraKey={realtimeCameraKey}
                        remoteCameraState={remoteCameraState}
                        onRealtimeCameraChange={onRealtimeCameraChange}
                        materialTexturePreset={materialTexturePreset}
                        materialTextureBinding={materialTextureBinding}
                        unpolishedSurfaceTexturePreset={unpolishedSurfaceTexturePreset}
                        surfaceFinishPolicy={surfaceFinishPolicy}
                    />
                ) : null}

                {loading ? (
                    <div className="adminProduct3DPreview__state">
                        <div className="adminProduct3DPreview__loader">
                            <span style={{ width: `${loadingPercent || 12}%` }} />
                        </div>

                        <strong>Загружаю 3D-модель</strong>
                        <p>
                            {loadingPercent
                                ? `${Math.round(loadingPercent)}%`
                                : 'Подготовка файла...'}
                        </p>
                    </div>
                ) : null}

                {!hasModel && !loading && loadError ? (
                    <div className="adminProduct3DPreview__state adminProduct3DPreview__state--error">
                        <strong>3D-модель не открылась</strong>
                        <p>{loadError}</p>
                    </div>
                ) : null}

                {!hasModel && !loading && !loadError ? (
                    <div className="adminProduct3DPreview__state">
                        <strong>Проверяю наличие 3D</strong>
                        <p>Если модель прикреплена к товару, она появится здесь.</p>
                    </div>
                ) : null}
            </div>

            <button
                type="button"
                className="adminProduct3DStage__fullscreen"
                data-catalog-sync-key={`scene-fullscreen-${stageKey}`}
                aria-label={isFullscreen
                    ? 'Вернуть 3D в окно'
                    : 'Развернуть 3D на весь экран'}
                aria-pressed={isFullscreen}
                title={isFullscreen
                    ? 'Вернуть 3D в окно (Esc)'
                    : 'Развернуть 3D на весь экран'}
                onClick={toggleFullscreen}
            >
                {isFullscreen ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" />
                        <path d="M9 9 3 3M15 9l6-6M9 15l-6 6M15 15l6 6" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M9 3H3v6M15 3h6v6M9 21H3v-6M15 21h6v-6" />
                    </svg>
                )}
            </button>

            <AdminProduct3DSceneTools
                stageKey={stageKey}
                viewMode={viewMode}
                onViewModeChange={onViewModeChange}
                sceneSettings={sceneSettings}
                onSceneSettingChange={onSceneSettingChange}
                onSceneSettingsChange={onSceneSettingsChange}
            />

            {graniteControls ? (
                <div className="adminProduct3DStage__granite">
                    {graniteControls}
                </div>
            ) : null}

            <div
                className={[
                    'adminProduct3DStage__displayMenu',
                    displayControlsOpen ? 'is-open' : '',
                ].filter(Boolean).join(' ')}
                aria-label="Отображение размеров и заготовки"
            >
                <button
                    type="button"
                    className="adminProduct3DStage__displayMenuToggle"
                    aria-expanded={displayControlsOpen}
                    aria-controls={`product-3d-display-controls-${stageKey}`}
                    title={displayControlsOpen
                        ? 'Скрыть настройки отображения'
                        : 'Показать настройки отображения'}
                    onClick={() => setDisplayControlsState(!displayControlsOpen)}
                >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z" />
                        <circle cx="12" cy="12" r="2.5" />
                    </svg>
                </button>

                {displayControlsOpen ? (
                    <div
                        id={`product-3d-display-controls-${stageKey}`}
                        className="adminProduct3DStage__displayMenuPanel"
                    >
                        <header>
                            <strong>Отображение</strong>
                            <button
                                type="button"
                                aria-label="Свернуть настройки отображения"
                                title="Свернуть"
                                onClick={() => setDisplayControlsState(false)}
                            >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="m7 10 5 5 5-5" />
                                </svg>
                            </button>
                        </header>

                        <button
                            type="button"
                            role="switch"
                            aria-checked={modelDimensionsVisible}
                            className={modelDimensionsVisible ? 'is-active' : ''}
                            disabled={!hasModel}
                            title={modelDimensionsVisible
                                ? 'Скрыть размеры изделия'
                                : 'Показать размеры изделия'}
                            data-catalog-sync-key={`scene-dimensions-${stageKey}`}
                            onClick={() => onShowDimensionsChange?.(!modelDimensionsVisible)}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 7V4h3M17 4h3v3M20 17v3h-3M7 20H4v-3" />
                                <path d="M7 12h10M9 9l-3 3 3 3M15 9l3 3-3 3" />
                            </svg>
                            <span>
                                <strong>Изделие</strong>
                                <small>Размеры модели</small>
                            </span>
                            <i aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            role="switch"
                            aria-checked={blankDimensionsVisible}
                            className={blankDimensionsVisible ? 'is-active' : ''}
                            disabled={!hasModel || !blankDimensions}
                            title={blankDimensionsVisible
                                ? 'Скрыть размеры заготовки'
                                : 'Показать размеры заготовки'}
                            data-catalog-sync-key={`scene-blank-dimensions-${stageKey}`}
                            onClick={() => onShowBlankDimensionsChange?.(!blankDimensionsVisible)}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M4 7V4h3M17 4h3v3M20 17v3h-3M7 20H4v-3" />
                                <path d="M7 12h10M9 9l-3 3 3 3M15 9l3 3-3 3" />
                            </svg>
                            <span>
                                <strong>Заготовка</strong>
                                <small>Размеры рамки</small>
                            </span>
                            <i aria-hidden="true" />
                        </button>

                        <button
                            type="button"
                            role="switch"
                            aria-checked={!!showBlankFrame}
                            className={showBlankFrame ? 'is-active' : ''}
                            disabled={!hasModel || !blankDimensions}
                            title={showBlankFrame
                                ? 'Скрыть контур заготовки'
                                : 'Показать контур заготовки'}
                            data-catalog-sync-key={`scene-blank-frame-${stageKey}`}
                            onClick={() => onShowBlankFrameChange?.(!showBlankFrame)}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <rect x="4" y="4" width="16" height="16" rx="2" />
                                <path d="M8 8h8v8H8z" />
                            </svg>
                            <span>
                                <strong>Рамка</strong>
                                <small>Контур плиты</small>
                            </span>
                            <i aria-hidden="true" />
                        </button>
                    </div>
                ) : null}
            </div>
        </section>
    );

    if (isFullscreen && typeof document !== 'undefined') {
        return (
            <>
                <div
                    className="adminProduct3DStage__placeholder"
                    aria-hidden="true"
                />
                {createPortal(renderStage(true), document.body)}
            </>
        );
    }

    return renderStage(false);
};

export default AdminProduct3DStage;
